export const defaultCameraNear = 28;
export const defaultCameraFar = 10000;

export interface CameraSettings {
  readonly name: string;
  readonly fov: number;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly targetX: number;
  readonly targetY: number;
  readonly targetZ: number;
  readonly upX: number;
  readonly upY: number;
  readonly upZ: number;
  readonly ortho: boolean;
  readonly minOrthoWidth?: number;
  readonly minOrthoHeight?: number;
  readonly cameraNear?: number;
  readonly cameraFar?: number;
}

export type FullyDefinedPerspectiveCameraSettings = CameraSettings & {
  readonly cameraNear: number;
  readonly cameraFar: number;
};

export const DEFAULT_MAX_POLAR_ANGLE = Math.PI / 2;

export const defaultCameraSettings: FullyDefinedPerspectiveCameraSettings = Object.freeze({
  name: "Overall",
  fov: 50,
  ortho: false,
  x: 700,
  y: -800,
  z: 500,
  upX: 0,
  upY: 0,
  upZ: 1,
  targetX: 0,
  targetY: 300,
  targetZ: 0,
  cameraNear: defaultCameraNear,
  cameraFar: defaultCameraFar,
});

// Orthographic camera with dynamic target
export function dynamicOrthographicCameraSettings(
  targetX: number,
  targetY: number,
  targetZ: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  upX: number,
  upY: number,
  upZ: number,
  minOrthoWidth: number | undefined,
  minOrthoHeight: number | undefined,
  distance: number = 1700,
  cameraNear: number = defaultCameraNear,
  cameraFar: number = defaultCameraFar,
): CameraSettings {
  return {
    ...defaultCameraSettings,
    name: "Plan",
    ortho: true,
    x: targetX - dirX * distance,
    y: targetY - dirY * distance,
    z: targetZ - dirZ * distance,
    targetX,
    targetY,
    targetZ,
    upX,
    upY,
    upZ,
    minOrthoWidth,
    minOrthoHeight,
    cameraNear,
    cameraFar,
  };
}

export const DEFAULT_ORTHO_CAMERA_WIDTH = 1500;

export const defaultOrthoCameraSettings = dynamicOrthographicCameraSettings(
  0,
  0,
  0,
  0,
  0,
  -1,
  0,
  1,
  0,
  DEFAULT_ORTHO_CAMERA_WIDTH,
  DEFAULT_ORTHO_CAMERA_WIDTH,
);

export function dynamicPerspectiveCameraSettings(
  targetX: number,
  targetY: number,
  targetZ: number,
  x: number,
  y: number,
  z: number,
  cameraNear?: number,
  cameraFar?: number,
  fov?: number,
): CameraSettings {
  const camera = {
    ...defaultCameraSettings,
    x,
    y,
    z,
    targetX,
    targetY,
    targetZ,
  };
  if (cameraNear) {
    camera.cameraNear = cameraNear;
  }
  if (cameraFar) {
    camera.cameraFar = cameraFar;
  }
  if (fov) {
    camera.fov = fov;
  }
  return camera;
}

export const defaultExteriorCameraPosition = {
  x: defaultCameraSettings.x,
  y: defaultCameraSettings.y,
  z: defaultCameraSettings.z,
  maxPolarAngle: DEFAULT_MAX_POLAR_ANGLE,
};

export function orthoCameraBounds(aspect: number, minOrthoWidth?: number, minOrthoHeight?: number) {
  // if ortho width/height is specified return dimensions scaled to aspect ratio
  let height: number = 1;
  let width: number = aspect;
  if (minOrthoWidth) {
    width = minOrthoWidth;
    height = width / aspect;
  }
  if (minOrthoHeight && (!minOrthoWidth || aspect * minOrthoHeight > minOrthoWidth)) {
    height = minOrthoHeight;
    width = aspect * height;
  }

  return {
    left: width / -2,
    right: width / 2,
    top: height / 2,
    bottom: height / -2,
  };
}
